import { env } from '../env'
import { runWhenActivated } from '../helpers/runWhenActivated'
import { isPublicPageBot } from '../helpers/publicPageBotGuard'
import {
  isAnalyticsConsented,
  whenAnalyticsConsented,
} from '../helpers/publicCookieConsent'

type CustomerIoMethod = (...args: unknown[]) => unknown

type CustomerIoLoadOptions = {
  integrations: {
    'Customer.io In-App Plugin': {
      siteId: string
    }
  }
}

type CustomerIoAnalytics = Window['cioanalytics'] &
  unknown[] & {
    initialize?: unknown
    invoked?: boolean
    methods?: string[]
    factory?: (event: string) => CustomerIoMethod
    load?: (key: string, options: CustomerIoLoadOptions) => void
    page?: CustomerIoMethod
    _writeKey?: string
    _loadOptions?: CustomerIoLoadOptions
    SNIPPET_VERSION?: string
  }

function isCustomerIoAnalytics(
  value: Window['cioanalytics'] | undefined,
): value is CustomerIoAnalytics {
  return Array.isArray(value)
}

;((): void => {
  // Same reasoning as the Segment guard in ./segment.ts: a recognizable bot on
  // the public /p pages gets no Customer.io CDP script and no event queue, only
  // a no-op `identify` so app-shell's Customer.io call stays safe.
  if (isPublicPageBot()) {
    window.cioanalytics = { identify: (): void => undefined }
    return
  }

  const globalName = 'cioanalytics'
  let analytics: CustomerIoAnalytics
  if (isCustomerIoAnalytics(window.cioanalytics)) {
    analytics = window.cioanalytics
  } else {
    analytics = Object.assign([], {
      identify: (_traits: object): void => undefined,
    })
    window.cioanalytics = analytics
  }

  if (analytics.initialize || analytics.invoked) return

  analytics.invoked = true
  const methods: string[] = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ]
  analytics.methods = methods

  const factory = (event: string): CustomerIoMethod => {
    return (...args: unknown[]): unknown => {
      if (!isAnalyticsConsented()) return analytics
      args.unshift(event)
      analytics.push(args)
      return analytics
    }
  }
  analytics.factory = factory

  for (const method of methods) {
    Object.assign(analytics, { [method]: factory(method) })
  }

  const load = (key: string, options: CustomerIoLoadOptions): void => {
    if (!isAnalyticsConsented()) return
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.setAttribute('data-global-customerio-analytics-key', globalName)
    script.src = `https://cdp.customer.io/v1/analytics-js/snippet/${key}/analytics.min.js`
    const firstScript = document.getElementsByTagName('script')[0]
    firstScript?.parentNode?.insertBefore(script, firstScript)
    Object.assign(analytics, {
      _writeKey: key,
      _loadOptions: options,
    })
  }
  analytics.load = load

  analytics.SNIPPET_VERSION = '4.15.3'
  const page = factory('page')
  analytics.page = page

  whenAnalyticsConsented(() => {
    runWhenActivated(document, () => {
      load(env.VITE_CUSTOMERIO_API_KEY, {
        integrations: {
          'Customer.io In-App Plugin': {
            siteId: env.VITE_CUSTOMERIO_SITE_ID,
          },
        },
      })
      page()
    })
  })
})()
